import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CalendarIcon,
  ClockIcon,
  FolderIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import { Task, Team } from '../types';
import { tasksAPI, teamsAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import UserCalendar from '../components/user/UserCalendar';
import MyTasks from '../components/user/MyTasks';
import toast from 'react-hot-toast';
import MyTeams from '../components/user/MyTeams';
import UserTaskModal from '../components/user/UserTaskModal';

type ActiveTab = 'overview' | 'tasks' | 'calendar' | 'teams' | 'projects';

const UserDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [myTasks, setMyTasks] = useState<Task[]>([]);
  const [myTeams, setMyTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Enhanced dashboard data
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      const [tasksData, teamsData, dashboardDataResponse] = await Promise.all([
        tasksAPI.getUserTasksEnhanced(user.id, {
          includeCompleted: false,
          sortBy: 'priority',
          sortOrder: 'desc'
        }),
        teamsAPI.getAll(),
        usersAPI.getUserDashboard(user.id),
      ]);

      setMyTasks(tasksData);
      // Filter teams where user is a member
      const userTeams = teamsData.filter(team =>
        team.members.some(member => member.user.id === user.id) ||
        team.manager.id === user.id
      );
      setMyTeams(userTeams);
      setDashboardData(dashboardDataResponse);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'tasks', name: 'My Tasks', icon: '📋' },
    { id: 'projects', name: 'My Projects', icon: '📁' },
    { id: 'calendar', name: 'Calendar', icon: '📅' },
    { id: 'teams', name: 'My Teams', icon: '👥' },
  ];

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
  };

  const handleTaskUpdate = (updatedTask: Task) => {
    // Update tasks in all relevant state
    setMyTasks(prev => prev.map(t => t.id === updatedTask.id ? updatedTask : t));

    if (dashboardData) {
      setDashboardData((prev: any) => ({
        ...prev,
        recentTasks: prev.recentTasks?.map((t: any) => t.id === updatedTask.id ? updatedTask : t) || [],
        upcomingDeadlines: prev.upcomingDeadlines?.map((t: any) => t.id === updatedTask.id ? updatedTask : t) || [],
      }));
    }

    // If the task was completed, show celebration and reload data
    if (updatedTask.status === 'DONE') {
      toast.success(`🎉 Great job! "${updatedTask.title}" is now complete!`);
      // Reload dashboard data to get updated statistics
      setTimeout(() => {
        loadData();
      }, 1000);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800';
      case 'TODO':
        return 'bg-gray-100 text-gray-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'text-red-600';
      case 'HIGH':
        return 'text-yellow-600';
      case 'NORMAL':
        return 'text-blue-600';
      case 'LOW':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  const completedTasks = myTasks.filter(task => task.status === 'DONE').length;
  const activeTasks = myTasks.filter(task => task.status !== 'DONE' && task.status !== 'CANCELLED').length;
  const urgentTasks = myTasks.filter(task => task.priority === 'URGENT' && task.status !== 'DONE').length;
  const overdueTasks = myTasks.filter(task => task.dueDate && isOverdue(task.dueDate) && task.status !== 'DONE').length;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="mt-2 text-gray-600">
            Here's what's happening with your work today
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as ActiveTab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Enhanced Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="card">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="p-3 rounded-lg bg-blue-500">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Active Tasks</p>
                      <p className="text-2xl font-bold text-gray-900">{activeTasks}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="p-3 rounded-lg bg-green-500">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Completed</p>
                      <p className="text-2xl font-bold text-gray-900">{completedTasks}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="p-3 rounded-lg bg-red-500">
                        <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Urgent</p>
                      <p className="text-2xl font-bold text-gray-900">{urgentTasks}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="p-3 rounded-lg bg-yellow-500">
                        <CalendarIcon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Overdue</p>
                      <p className="text-2xl font-bold text-gray-900">{overdueTasks}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Metrics */}
              {dashboardData?.statistics && (
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Overview</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {Math.round(dashboardData.statistics.completionRate)}%
                      </div>
                      <div className="text-sm text-gray-500">Completion Rate</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {dashboardData.statistics.totalTasks}
                      </div>
                      <div className="text-sm text-gray-500">Total Tasks</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600">
                        {dashboardData.statistics.memberOfTeams}
                      </div>
                      <div className="text-sm text-gray-500">Teams</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Critical Tasks and Upcoming Deadlines */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="card">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Critical Tasks</h3>
                      <p className="text-xs text-gray-500 mt-1">Click on any task to view details</p>
                    </div>
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="space-y-3">
                    {dashboardData?.criticalTasks?.length > 0 ? (
                      dashboardData.criticalTasks.slice(0, 5).map((task: any) => (
                        <div
                          key={task.id}
                          className="flex items-center space-x-3 p-3 rounded-lg hover:bg-red-50 hover:shadow-md cursor-pointer transition-all duration-200 border-l-4 border-red-400 group"
                          onClick={() => handleTaskClick(task)}
                          title="Click to view task details"
                        >
                          <div className="flex-shrink-0">
                            <div className={`w-3 h-3 rounded-full ${
                              task.status === 'DONE' ? 'bg-green-400' :
                              task.status === 'IN_PROGRESS' ? 'bg-blue-400' :
                              task.priority === 'URGENT' ? 'bg-red-500' :
                              'bg-yellow-400'
                            }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate group-hover:text-red-700">
                              {task.title}
                            </p>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(task.status)}`}>
                                {task.status.replace('_', ' ')}
                              </span>
                              {task.team && (
                                <span className="text-xs text-gray-500">{task.team.name}</span>
                              )}
                              {task.dueDate && (
                                <>
                                  <span className="text-xs text-gray-400">•</span>
                                  <span className={`text-xs ${
                                    isOverdue(task.dueDate) ? 'text-red-600 font-medium' : 'text-gray-500'
                                  }`}>
                                    Due {formatDate(task.dueDate)}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className={`text-xs font-medium ${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </div>
                            <EyeIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      ))
                    ) : (
                      // Fallback to urgent/high priority tasks from myTasks
                      myTasks.filter(task =>
                        (task.priority === 'URGENT' || task.priority === 'HIGH') &&
                        task.status !== 'DONE'
                      ).slice(0, 5).map((task: any) => (
                        <div
                          key={task.id}
                          className="flex items-center space-x-3 p-3 rounded-lg hover:bg-yellow-50 hover:shadow-md cursor-pointer transition-all duration-200 border-l-4 border-yellow-400 group"
                          onClick={() => handleTaskClick(task)}
                          title="Click to view task details"
                        >
                          <div className="flex-shrink-0">
                            <div className={`w-3 h-3 rounded-full ${
                              task.status === 'DONE' ? 'bg-green-400' :
                              task.status === 'IN_PROGRESS' ? 'bg-blue-400' :
                              task.priority === 'URGENT' ? 'bg-red-500' :
                              'bg-yellow-400'
                            }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate group-hover:text-yellow-700">
                              {task.title}
                            </p>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(task.status)}`}>
                                {task.status.replace('_', ' ')}
                              </span>
                              {task.team && (
                                <span className="text-xs text-gray-500">{task.team.name}</span>
                              )}
                              {task.dueDate && (
                                <>
                                  <span className="text-xs text-gray-400">•</span>
                                  <span className={`text-xs ${
                                    isOverdue(task.dueDate) ? 'text-red-600 font-medium' : 'text-gray-500'
                                  }`}>
                                    Due {formatDate(task.dueDate)}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className={`text-xs font-medium ${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </div>
                            <EyeIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      )) || (
                        <div className="text-center py-8">
                          <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">No critical tasks</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            Great job! You don't have any urgent or high-priority tasks right now.
                          </p>
                        </div>
                      )
                    )}
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Upcoming Deadlines</h3>
                      <p className="text-xs text-gray-500 mt-1">Click on any task to view details</p>
                    </div>
                    <ExclamationTriangleIcon className="w-5 h-5 text-gray-400" />
                  </div>
                  <div className="space-y-3">
                    {dashboardData?.upcomingDeadlines?.slice(0, 5).map((task: any) => (
                      <div
                        key={task.id}
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 hover:shadow-md cursor-pointer transition-all duration-200 group"
                        onClick={() => handleTaskClick(task)}
                        title="Click to view task details"
                      >
                        <div className="flex-shrink-0">
                          <CalendarIcon className={`w-4 h-4 ${
                            isOverdue(task.dueDate) ? 'text-red-500' : 'text-blue-500'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-700">
                            {task.title}
                          </p>
                          <p className={`text-xs ${
                            isOverdue(task.dueDate) ? 'text-red-600 font-medium' : 'text-gray-500'
                          }`}>
                            Due {formatDate(task.dueDate || '')}
                            {isOverdue(task.dueDate) && ' (Overdue)'}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className={`text-xs font-medium ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </div>
                          <EyeIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    )) || myTasks.filter(task => task.dueDate && !isOverdue(task.dueDate) && task.status !== 'DONE').slice(0, 5).map((task: any) => (
                      <div
                        key={task.id}
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 hover:shadow-md cursor-pointer transition-all duration-200 group"
                        onClick={() => handleTaskClick(task)}
                        title="Click to view task details"
                      >
                        <div className="flex-shrink-0">
                          <CalendarIcon className="w-4 h-4 text-blue-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-700">
                            {task.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            Due {formatDate(task.dueDate || '')}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className={`text-xs font-medium ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </div>
                          <EyeIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Projects and Teams Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">My Projects</h3>
                    <FolderIcon className="w-5 h-5 text-gray-400" />
                  </div>
                  <div className="space-y-3">
                    {dashboardData?.projects?.slice(0, 4).map((project: any) => (
                      <div key={project.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {project.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {project.userTaskCount} of {project.totalTaskCount} tasks assigned to you
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {project.teams.map((pt: any) => (
                            <div
                              key={pt.team.id}
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: pt.team.color }}
                              title={pt.team.name}
                            />
                          ))}
                        </div>
                      </div>
                    )) || (
                      <div className="text-center py-4 text-gray-500">
                        <FolderIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">No projects assigned yet</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Team Insights</h3>
                    <UserGroupIcon className="w-5 h-5 text-gray-400" />
                  </div>
                  <div className="space-y-3">
                    {dashboardData?.teams?.slice(0, 4).map((team: any) => (
                      <div key={team.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: team.color }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {team.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {team.insights.userTasks} of {team.insights.totalTasks} tasks
                              {team.insights.isManager && ' (Manager)'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {Math.round(team.insights.completionRate)}%
                          </div>
                          <div className="text-xs text-gray-500">Complete</div>
                        </div>
                      </div>
                    )) || myTeams.slice(0, 4).map((team: any) => (
                      <div key={team.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: team.color }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {team.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {team.members.length} members
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tasks' && (
            <MyTasks
              tasks={myTasks}
              onTasksChange={setMyTasks}
              onTaskClick={handleTaskClick}
            />
          )}

          {activeTab === 'projects' && dashboardData?.projects && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">My Projects</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {dashboardData.projects.map((project: any) => (
                  <div key={project.id} className="card">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {project.name}
                      </h3>
                      <div className="flex space-x-1">
                        {project.teams.map((pt: any) => (
                          <div
                            key={pt.team.id}
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: pt.team.color }}
                            title={pt.team.name}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {project.description || 'No description available'}
                    </p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Your Tasks</span>
                        <span className="font-medium">{project.userTaskCount} of {project.totalTaskCount}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${project.totalTaskCount > 0 ? (project.userTaskCount / project.totalTaskCount) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                    {project.tasks.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Your Tasks</h4>
                        <div className="space-y-1">
                          {project.tasks.slice(0, 3).map((task: any) => (
                            <div
                              key={task.id}
                              className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-blue-50 hover:shadow-sm p-2 rounded transition-all duration-200"
                              onClick={() => handleTaskClick(task)}
                              title="Click to view task details"
                            >
                              <div className={`w-2 h-2 rounded-full ${
                                task.status === 'DONE' ? 'bg-green-400' :
                                task.status === 'IN_PROGRESS' ? 'bg-blue-400' :
                                'bg-gray-400'
                              }`} />
                              <span className="truncate hover:text-blue-700 transition-colors">{task.title}</span>
                            </div>
                          ))}
                          {project.tasks.length > 3 && (
                            <p className="text-xs text-gray-500">+{project.tasks.length - 3} more tasks</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'calendar' && (
            <UserCalendar tasks={myTasks} />
          )}

          {activeTab === 'teams' && (
            <MyTeams teams={myTeams} />
          )}
        </motion.div>

        {/* Task Detail Modal */}
        <UserTaskModal
          isOpen={isTaskModalOpen}
          onClose={() => {
            setIsTaskModalOpen(false);
            setSelectedTask(null);
          }}
          task={selectedTask}
          onTaskUpdate={handleTaskUpdate}
        />
      </div>
    </Layout>
  );
};

export default UserDashboard;
